import { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";

import EmptyComponent from "@/components/containers/EmptyComponent";
import Loading from "@/components/loadings/Loading";

import { useRecentSearchedUsers } from "../../hooks/useRecentSearchedUsers";
import { useFetchConversations } from "../../services";
import { type Conversation } from "../../types";
import ConversationCard from "./ConversationCard";

export default function ConversationList({
  open,
  onConversationClick,
  type,
  activeConversationId,
}: {
  open: boolean;
  onConversationClick: (conversation: Conversation<true, true, true>) => void;
  type?: string;
  activeConversationId?: number;
}) {
  const { t } = useTranslation("messenger.user");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open]);

  const { addRecentUser } = useRecentSearchedUsers();

  const {
    data: conversationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchConversations(type ? { filters: { type } } : undefined);

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto">
      <InfiniteScroll
        pageStart={0}
        loadMore={() => !isFetchingNextPage && fetchNextPage()}
        hasMore={hasNextPage}
        useWindow={false}
        className="flex flex-col"
        loader={<Loading key={0} className="my-2 self-center" size="sm" />}
      >
        {conversationsData?.pages?.[0]?.data ? (
          conversationsData.pages[0].data.length > 0 ? (
            conversationsData?.pages?.map((group, i) => (
              <Fragment key={i}>
                {group?.data?.map((conversation) => (
                  <div key={conversation.id} className="px-1.5 py-0.5">
                    <ConversationCard
                      key={conversation.id}
                      conversation={conversation}
                      isActive={conversation.id === activeConversationId}
                      onClick={() => {
                        if (conversation.type === "private")
                          addRecentUser(conversation.user);
                        onConversationClick(conversation);
                      }}
                    />
                  </div>
                ))}
              </Fragment>
            ))
          ) : (
            <EmptyComponent
              variant="cloud"
              title={t("No conversations found")}
              size="sm"
            />
          )
        ) : (
          <div className="p-4 text-center text-gray-500">
            {t("Loading conversations...")}
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
}
